import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Settings, Mail, Clock, CheckCircle, XCircle, RefreshCw, Play } from 'lucide-react';

const TimeAuditConfiguration = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    imapHost: '',
    imapPort: 993,
    imapUser: '',
    imapPassword: '',
    imapSecure: true,
    checkIntervalMinutes: 5,
    isEnabled: false
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/time-audit-config');
      setConfig(response.data);
      setFormData({
        imapHost: response.data.imapHost || '',
        imapPort: response.data.imapPort || 993,
        imapUser: response.data.imapUser || '',
        imapPassword: response.data.imapPassword || '',
        imapSecure: response.data.imapSecure !== false,
        checkIntervalMinutes: response.data.checkIntervalMinutes || 5,
        isEnabled: response.data.isEnabled || false
      });
    } catch (error) {
      console.error('Error fetching config:', error);
      setMessage({ type: 'error', text: 'Failed to load configuration' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) || '' : value)
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      const response = await axios.put('/api/time-audit-config', formData);
      setConfig(response.data.config);
      setMessage({ type: 'success', text: 'Configuration saved successfully' });
    } catch (error) {
      console.error('Error saving config:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to save configuration' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setMessage({ type: '', text: '' });
      const response = await axios.post('/api/time-audit-config/test-connection', formData);
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Connection successful!' });
      } else {
        setMessage({ type: 'error', text: response.data.error || 'Connection failed' });
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  const handleCheckNow = async () => {
    try {
      setChecking(true);
      setMessage({ type: '', text: '' });
      const response = await axios.post('/api/time-audit-config/check-now');
      if (response.data.success) {
        setMessage({
          type: 'success',
          text: `Check completed. Processed ${response.data.processed} email(s).${response.data.errors?.length ? ` Errors: ${response.data.errors.length}` : ''}`
        });
      } else {
        setMessage({ type: 'error', text: response.data.message || response.data.error || 'Check failed' });
      }
      fetchConfig(); // Refresh to get updated lastCheckedAt
    } catch (error) {
      console.error('Error checking emails:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to check emails' });
    } finally {
      setChecking(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-section">
        <h2><Settings size={20} /> Time Audit Configuration</h2>
        <div className="loading">Loading configuration...</div>
      </div>
    );
  }

  return (
    <div className="settings-section">
      <h2><Settings size={20} /> Time Audit Email Configuration</h2>
      <p style={{ color: '#666', marginBottom: '20px' }}>
        Configure the email inbox that will receive time clock Excel files from locations.
      </p>

      {message.text && (
        <div className={`alert ${message.type === 'error' ? 'alert-error' : 'alert-success'}`} style={{ marginBottom: '20px' }}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <XCircle size={16} />}
          <span style={{ marginLeft: '8px' }}>{message.text}</span>
        </div>
      )}

      {/* Status Card */}
      <div style={{
        backgroundColor: config?.isEnabled && config?.isConfigured ? '#dcfce7' : '#fef3c7',
        border: `1px solid ${config?.isEnabled && config?.isConfigured ? '#86efac' : '#fcd34d'}`,
        borderRadius: '8px',
        padding: '15px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {config?.isEnabled && config?.isConfigured ? (
            <CheckCircle size={20} color="#16a34a" />
          ) : (
            <XCircle size={20} color="#d97706" />
          )}
          <span style={{ fontWeight: 'bold' }}>
            {config?.isEnabled && config?.isConfigured ? 'Email monitoring is active' : 'Email monitoring is inactive'}
          </span>
        </div>
        {config?.schedulerStatus && (
          <div style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
            Scheduler: {config.schedulerStatus.emailCheckEnabled ? 'Running' : 'Stopped'}
            {config.lastCheckedAt && (
              <span style={{ marginLeft: '15px' }}>
                Last checked: {new Date(config.lastCheckedAt).toLocaleString()}
              </span>
            )}
          </div>
        )}
      </div>

      {/* IMAP Settings */}
      <div style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
        <h3 style={{ margin: '0 0 15px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Mail size={18} /> IMAP Server Settings
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
          <div className="form-group">
            <label>IMAP Host</label>
            <input
              type="text"
              name="imapHost"
              className="form-control"
              value={formData.imapHost}
              onChange={handleChange}
              placeholder="imap.gmail.com"
            />
          </div>

          <div className="form-group">
            <label>IMAP Port</label>
            <input
              type="number"
              name="imapPort"
              className="form-control"
              value={formData.imapPort}
              onChange={handleChange}
              placeholder="993"
            />
          </div>

          <div className="form-group">
            <label>Email Address</label>
            <input
              type="email"
              name="imapUser"
              className="form-control"
              value={formData.imapUser}
              onChange={handleChange}
              placeholder="timeaudit@company.com"
            />
          </div>

          <div className="form-group">
            <label>Password / App Password</label>
            <input
              type="password"
              name="imapPassword"
              className="form-control"
              value={formData.imapPassword}
              onChange={handleChange}
              placeholder={config?.hasPassword ? '********' : 'Enter password'}
            />
          </div>
        </div>

        <div className="form-group" style={{ marginTop: '10px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              name="imapSecure"
              checked={formData.imapSecure}
              onChange={handleChange}
              style={{ marginRight: '8px' }}
            />
            Use SSL/TLS (recommended)
          </label>
        </div>

        <button
          className="btn btn-secondary"
          onClick={handleTestConnection}
          disabled={testing || !formData.imapHost || !formData.imapUser}
          style={{ marginTop: '10px' }}
        >
          {testing ? <><RefreshCw size={14} className="spin" /> Testing...</> : 'Test Connection'}
        </button>
      </div>

      {/* Schedule Settings */}
      <div style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
        <h3 style={{ margin: '0 0 15px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Clock size={18} /> Schedule Settings
        </h3>

        <div className="form-group">
          <label>Check Interval (minutes)</label>
          <input
            type="number"
            name="checkIntervalMinutes"
            className="form-control"
            value={formData.checkIntervalMinutes}
            onChange={handleChange}
            min="1"
            max="60"
            style={{ width: '150px' }}
          />
          <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
            How often to check for new emails (1-60 minutes)
          </small>
        </div>

        <div className="form-group" style={{ marginTop: '15px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              name="isEnabled"
              checked={formData.isEnabled}
              onChange={handleChange}
              style={{ marginRight: '8px' }}
            />
            Enable automatic email checking
          </label>
        </div>

        {config?.isConfigured && (
          <button
            className="btn btn-secondary"
            onClick={handleCheckNow}
            disabled={checking}
            style={{ marginTop: '10px' }}
          >
            {checking ? <><RefreshCw size={14} className="spin" /> Checking...</> : <><Play size={14} /> Check Now</>}
          </button>
        )}
      </div>

      {/* Save Button */}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default TimeAuditConfiguration;
